import { inject, Injectable, InjectionToken } from "@angular/core";
import { environment } from "@environments/environment";
import { scrub } from "@core/logging/log-scrubber";
import {
  ErrorReporter,
  LogContext,
  LogLevel,
} from "@core/logging/logger.types";

export const ERROR_REPORTER = new InjectionToken<ErrorReporter>(
  "ERROR_REPORTER"
);

function resolveLogLevel(level: string): LogLevel {
  const levels: Record<string, LogLevel> = {
    DEBUG: LogLevel.DEBUG,
    INFO: LogLevel.INFO,
    WARN: LogLevel.WARN,
    ERROR: LogLevel.ERROR,
    FATAL: LogLevel.FATAL,
  };
  return levels[level] ?? LogLevel.WARN;
}

interface ResolvedLogConfig {
  level: LogLevel;
  console: boolean;
  remote: boolean;
}

@Injectable({ providedIn: "root" })
export class LoggerService {
  private readonly config: ResolvedLogConfig;
  private readonly errorReporter = inject(ERROR_REPORTER, { optional: true });

  constructor() {
    const envConfig = environment.LOG_CONFIG;
    this.config = {
      ...envConfig,
      level: resolveLogLevel(envConfig.level),
    };
  }

  createContext(name: string): LogContext {
    return {
      debug: (msg, data) => this.emit(LogLevel.DEBUG, name, msg, undefined, data),
      info: (msg, data) => this.emit(LogLevel.INFO, name, msg, undefined, data),
      warn: (msg, data) => this.emit(LogLevel.WARN, name, msg, undefined, data),
      error: (msg, err, data) =>
        this.emit(LogLevel.ERROR, name, msg, err, data),
      fatal: (msg, err, data) =>
        this.emit(LogLevel.FATAL, name, msg, err, data),
    };
  }

  private emit(
    level: LogLevel,
    contextName: string,
    msg: string,
    err?: unknown,
    data?: unknown
  ): void {
    if (level < this.config.level) {
      return;
    }

    const shouldScrub = level >= LogLevel.INFO;
    const prefix = `[${contextName}] ${msg}`;
    const scrubbedErr =
      shouldScrub && err !== undefined ? scrub(err) : err;
    const scrubbedData =
      shouldScrub && data !== undefined ? scrub(data) : data;

    if (this.config.console) {
      this.writeToConsole(level, prefix, scrubbedErr, scrubbedData);
    }

    if (this.config.remote && level >= LogLevel.ERROR) {
      this.errorReporter?.captureException(err ?? msg, {
        context: contextName,
        level: LogLevel[level],
        data: scrubbedData,
      });
    }
  }

  /* eslint-disable no-console -- the single sanctioned console sink; all
     other code must log via LoggerService*/
  private writeToConsole(
    level: LogLevel,
    msg: string,
    err?: unknown,
    data?: unknown
  ): void {
    const consoleWriters: Record<LogLevel, (...args: unknown[]) => void> = {
      [LogLevel.DEBUG]: console.debug,
      [LogLevel.INFO]: console.info,
      [LogLevel.WARN]: console.warn,
      [LogLevel.ERROR]: console.error,
      [LogLevel.FATAL]: console.error,
    };
    const write = consoleWriters[level] ?? console.error;

    if (err !== undefined && data !== undefined) {
      write(msg, err, data);
      return;
    }

    if (err !== undefined) {
      write(msg, err);
      return;
    }

    if (data !== undefined) {
      write(msg, data);
      return;
    }

    write(msg);
  }
  /* eslint-enable no-console */
}
